import React, { useState, useEffect } from 'react';
import './WorkoutTracker.css';
import { WorkoutProgress } from '../models/WorkoutProgress';
import SessionTracker from './SessionTracker';
import { StorageService } from '../services/StorageService';
import { saveExerciseHistory } from '../services/FirebaseService';
import { EXERCISE_NAMES, getExerciseByName } from '../data/exercises';

function WorkoutTracker({ onClose, authUserId, onFinishRoutine }) {
  const [currentWeek, setCurrentWeek] = useState(1);
  const [workoutLog, setWorkoutLog] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [routines, setRoutines] = useState([]);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [showSessionTracker, setShowSessionTracker] = useState(false);
  const [expandLogForm, setExpandLogForm] = useState(false);
  const [exerciseHistory, setExerciseHistory] = useState({});
  const [completedRoutineId, setCompletedRoutineId] = useState(null);

  useEffect(() => {
    // Load workout log from localStorage
    const saved = localStorage.getItem('workout_log');
    if (saved) {
      setWorkoutLog(JSON.parse(saved));
    }

    const savedWeek = localStorage.getItem('current_week');
    if (savedWeek) {
      setCurrentWeek(parseInt(savedWeek));
    }

    // Load saved routines
    const savedRoutines = localStorage.getItem('workout_routines');
    if (savedRoutines) {
      setRoutines(JSON.parse(savedRoutines));
    }

    setExerciseHistory(StorageService.getExerciseHistory());
  }, []);

  useEffect(() => {
    if (!selectedExercise) return;
    const historyEntry = exerciseHistory[selectedExercise];
    if (!historyEntry) {
      const defaults = getExerciseByName(selectedExercise);
      setSets(defaults?.sets ?? 3);
      setReps(defaults?.reps ?? 10);
      setWeight('');
      return;
    }

    const nextSets = Number.isFinite(historyEntry.sets)
      ? historyEntry.sets
      : parseInt(historyEntry.sets, 10) || 3;
    const nextReps = Number.isFinite(historyEntry.reps)
      ? historyEntry.reps
      : parseInt(historyEntry.reps, 10) || 10;
    const nextWeight = historyEntry.weight ?? '';

    setSets(nextSets);
    setReps(nextReps);
    setWeight(nextWeight);
  }, [selectedExercise, exerciseHistory]);

  const persistExerciseHistory = async (nextHistory) => {
    setExerciseHistory(nextHistory);
    StorageService.saveExerciseHistory(nextHistory);
    if (authUserId) {
      try {
        // Sync to Realtime Database
        await saveExerciseHistory(authUserId, nextHistory);
      } catch (error) {
        console.warn('Failed to sync exercise history to RTDB:', error);
      }
      try {
        // Sync to Firestore
        await saveExerciseHistory(authUserId, nextHistory);
      } catch (error) {
        console.warn('Failed to sync exercise history to Firestore:', error);
      }
    }
  };

  const updateExerciseHistoryEntry = (exerciseName, entry) => {
    if (!exerciseName) return;
    const updatedEntry = {
      ...entry,
      updatedAt: entry?.updatedAt || new Date().toISOString()
    };
    const nextHistory = {
      ...exerciseHistory,
      [exerciseName]: updatedEntry
    };
    persistExerciseHistory(nextHistory);
  };

  const updateExerciseHistoryEntries = (entriesMap) => {
    if (!entriesMap || typeof entriesMap !== 'object') return;
    const nextHistory = {
      ...exerciseHistory,
      ...entriesMap
    };
    persistExerciseHistory(nextHistory);
  };

  const getLastNumericValue = (setsList, field) => {
    if (!Array.isArray(setsList)) return null;
    for (let i = setsList.length - 1; i >= 0; i -= 1) {
      const value = setsList[i]?.[field];
      if (Number.isFinite(value)) {
        return value;
      }
    }
    return null;
  };

  const exercises = EXERCISE_NAMES;

  const logWorkout = (e) => {
    e.preventDefault();

    const normalizedWeight = weight === '' || weight === null ? null : Number(weight);
    updateExerciseHistoryEntry(selectedExercise, {
      sets,
      reps,
      weight: normalizedWeight
    });
    
    const progress = new WorkoutProgress({
      exerciseName: selectedExercise,
      sets,
      reps,
      weight: normalizedWeight ?? 0,
      notes,
      completed: true
    });

    const updated = [...workoutLog, progress];
    setWorkoutLog(updated);
    localStorage.setItem('workout_log', JSON.stringify(updated));

    // Reset form
    setSets(3);
    setReps(10);
    setWeight('');
    setNotes('');
  };

  const getWeeklyProgress = () => {
    const now = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const startOfWeek = now - (now % weekMs);
    
    return workoutLog.filter(log => {
      const logDate = new Date(log.date).getTime();
      return logDate >= startOfWeek;
    });
  };

  const handleLogWorkout = (session, xpReward) => {
    const historyUpdates = {};

    // Log each exercise from the session to workout log
    session.exercises.forEach((exercise) => {
      const lastReps = getLastNumericValue(exercise.sets, 'reps');
      const lastWeight = getLastNumericValue(exercise.sets, 'weight');
      const entry = {
        sets: exercise.sets?.length || exercise.targetSets || 0,
        reps: lastReps ?? exercise.targetReps ?? 0,
        weight: lastWeight ?? exercise.defaultWeight ?? null,
        updatedAt: new Date().toISOString()
      };
      historyUpdates[exercise.exerciseName] = entry;

      const progress = new WorkoutProgress({
        exerciseName: exercise.exerciseName,
        sets: exercise.targetSets,
        reps: exercise.targetReps,
        weight: exercise.defaultWeight,
        completed: true
      });
      workoutLog.push(progress);
    });

    updateExerciseHistoryEntries(historyUpdates);

    const updated = [...workoutLog];
    setWorkoutLog(updated);
    localStorage.setItem('workout_log', JSON.stringify(updated));

    // Create or update a task for completing the session
    const completedTask = {
      title: `${session.name} - Completed`,
      description: `Completed workout session with ${session.exercises.length} exercises`,
      type: 'exercise',
      difficulty: Math.min(5, Math.ceil(session.exercises.length / 3)),
      xpReward,
      completed: true,
      completedAt: new Date().toISOString()
    };

    // Save to completed tasks
    const completedTasks = JSON.parse(localStorage.getItem('completed_tasks') || '[]');
    completedTasks.push(completedTask);
    localStorage.setItem('completed_tasks', JSON.stringify(completedTasks));

    if (selectedRoutine?.id) {
      setCompletedRoutineId(selectedRoutine.id);
    }

    // Dispatch event to notify dashboard of XP update
    window.dispatchEvent(new CustomEvent('workoutLogged', { detail: { xp: xpReward } }));
  };

  const handleStartSession = (routine) => {
    setSelectedRoutine(routine);
    setShowSessionTracker(true);
  };

  const handleDeleteRoutine = (routineId) => {
    const updated = routines.filter(r => r.id !== routineId);
    setRoutines(updated);
    localStorage.setItem('workout_routines', JSON.stringify(updated));
    if (completedRoutineId === routineId) {
      setCompletedRoutineId(null);
    }
  };

  const handleFinishRoutine = (routine) => {
    if (onFinishRoutine) {
      onFinishRoutine(routine);
    }
    setCompletedRoutineId(null);
    if (onClose) {
      onClose();
    }
  };

  const weeklyLogs = getWeeklyProgress();

  return (
    <div className="workout-tracker-overlay" onClick={onClose}>
      <div className="workout-tracker" onClick={(e) => e.stopPropagation()}>
        <div className="tracker-header">
          <h2> Workout Tracker - Week {currentWeek}</h2>
          <button className="close-btn" onClick={onClose}></button>
        </div>

        {/* Show Session Tracker if active */}
        {showSessionTracker && selectedRoutine && (
          <SessionTracker 
            routine={selectedRoutine}
            onClose={() => {
              setShowSessionTracker(false);
              setSelectedRoutine(null);
            }}
            onLogWorkout={handleLogWorkout}
          />
        )}

        {!showSessionTracker && (
          <>
            {/* Week Navigation */}
            <div className="week-nav">
              <button 
                onClick={() => {
                  const newWeek = Math.max(1, currentWeek - 1);
                  setCurrentWeek(newWeek);
                  localStorage.setItem('current_week', newWeek);
                }}
              >
                 Previous Week
              </button>
              <span className="week-display">Week {currentWeek} / 4</span>
              <button 
                onClick={() => {
                  const newWeek = Math.min(4, currentWeek + 1);
                  setCurrentWeek(newWeek);
                  localStorage.setItem('current_week', newWeek);
                }}
                disabled={currentWeek >= 4}
              >
                Next Week 
              </button>
            </div>

            {/* Progress This Week */}
            <div className="weekly-summary">
              <h3>This Week's Progress</h3>
              <p>{weeklyLogs.length} workouts logged</p>
            </div>

            {/* Saved Routines Section */}
            {routines.length > 0 && (
              <div className="saved-routines">
                <h3> Your Routines</h3>
                <div className="routines-list">
                  {routines.map((routine) => (
                    <div key={routine.id} className="routine-card">
                      <div className="routine-info">
                        <h4>{routine.name}</h4>
                        <p>{routine.exercises?.length || 0} exercises</p>
                      </div>
                      <div className="routine-actions">
                        {completedRoutineId === routine.id ? (
                          <button
                            className="btn-finish-session"
                            onClick={() => handleFinishRoutine(routine)}
                          >
                             Finish
                          </button>
                        ) : (
                          <button
                            className="btn-start-session"
                            onClick={() => handleStartSession(routine)}
                          >
                             Start
                          </button>
                        )}
                        <button
                          className="btn-delete-routine"
                          onClick={() => handleDeleteRoutine(routine.id)}
                        >
                           Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Log Workout Form */}
            <div className="log-form-container">
              <div 
                className="log-form-header"
                onClick={() => setExpandLogForm(!expandLogForm)}
              >
                <h3> Log Today's Workout</h3>
                <span className={`expand-arrow ${expandLogForm ? 'expanded' : ''}`}></span>
              </div>

              {expandLogForm && (
                <form onSubmit={logWorkout} className="log-form">
                  <div className="form-group">
                    <label>Exercise</label>
                    <select 
                      value={selectedExercise} 
                      onChange={(e) => setSelectedExercise(e.target.value)}
                      required
                    >
                      <option value="">Select exercise...</option>
                      {exercises.map(ex => (
                        <option key={ex} value={ex}>{ex}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Sets</label>
                      <input 
                        type="number" 
                        value={sets} 
                        onChange={(e) => setSets(parseInt(e.target.value))}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Reps</label>
                      <input 
                        type="number" 
                        value={reps} 
                        onChange={(e) => setReps(parseInt(e.target.value))}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Weight (lbs)</label>
                      <input 
                        type="number" 
                        value={weight ?? ''} 
                        onChange={(e) => {
                          const next = e.target.value;
                          setWeight(next === '' ? '' : parseFloat(next));
                        }}
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Notes (optional)</label>
                    <textarea 
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="How did it feel? Any adjustments?"
                      rows="2"
                    />
                  </div>

                  <button type="submit" className="submit-btn">
                     Log Workout
                  </button>
                </form>
              )}
            </div>

            {/* Recent Logs */}
            {weeklyLogs.length > 0 && (
              <div className="recent-logs">
                <h3>Recent Workouts</h3>
                <div className="logs-list">
                  {weeklyLogs.slice(-5).reverse().map((log, idx) => (
                    <div key={idx} className="log-item">
                      <div className="log-header">
                        <strong>{log.exerciseName}</strong>
                        <span className="log-date">
                          {new Date(log.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="log-details">
                        {log.sets} sets  {log.reps} reps
                        {log.weight > 0 && ` @ ${log.weight} lbs`}
                      </div>
                      {log.notes && <p className="log-notes">{log.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progression Tips */}
            <div className="tips-section">
              <h3> Progression Tips</h3>
              <ul>
                <li> Increase weight by 2.5-5 lbs every 2-3 weeks</li>
                <li> Add 1-2 reps when weight feels easier</li>
                <li> Focus on form over weight</li>
                <li> Rest 60-90 seconds between sets</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WorkoutTracker;
